import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import { LeadSourceReport } from '../../../types/reporting';
import { useTheme } from '@mui/material/styles';
import { reportingService } from '../../../services/reportingService';

interface LeadSourceChartProps {
  data?: LeadSourceReport[];
  loading?: boolean;
}

const COLORS = [
  '#2196F3', // Blue
  '#4CAF50', // Green
  '#FF9800', // Orange
  '#9C27B0', // Purple
  '#F44336', // Red
  '#00BCD4', // Cyan
  '#795548', // Brown
  '#607D8B', // Blue Grey
];

const LeadSourceChart: React.FC<LeadSourceChartProps> = ({ data, loading = false }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="circular" width={200} height={200} sx={{ mt: 2, mx: 'auto' }} />
        </CardContent>
      </Card>
    );
  }

  const chartData = data?.map((source, index) => ({
    name: source.sourceName || 'Unknown',
    value: source.totalValue,
    customerCount: source.customerCount,
    jobCount: source.jobCount,
    color: COLORS[index % COLORS.length],
  })) || [];

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Lead Source Distribution
        </Typography>
        <Box sx={{ width: '100%', height: 300 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 4,
                  }}
                  formatter={(value: number, name: string, entry: any) => {
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                    return [
                      <>
                        <div>{reportingService.formatCurrency(value)}</div>
                        <div style={{ fontSize: '0.85em', color: theme.palette.text.secondary }}>
                          {percentage}% of total
                        </div>
                        <div style={{ fontSize: '0.85em', color: theme.palette.text.secondary }}>
                          {entry.payload.jobCount} jobs from {entry.payload.customerCount} customers
                        </div>
                      </>,
                      entry.payload.name,
                    ];
                  }}
                />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  formatter={(value: string, entry: any) => (
                    <span style={{ color: theme.palette.text.primary, fontSize: 12 }}>
                      {value}
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography color="text.secondary">No data available</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default LeadSourceChart;
